import type { ReactNode } from 'react';

import styles from './Card.module.css';

export type CardSurface = 'default' | 'elevated' | 'muted';

export interface CardProps {
  readonly surface?: CardSurface;
  readonly compact?: boolean;
  /** Rendered as a heading; pair with `headingLevel` to keep document order sane. */
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Heading level for `title`. Defaults to 2 — never hardcode h1 in a panel. */
  readonly headingLevel?: 2 | 3 | 4;
  readonly actions?: ReactNode;
  readonly footer?: ReactNode;
  readonly children?: ReactNode;
}

/** Grouping surface — design addon §8.2. */
export function Card({
  surface = 'default',
  compact = false,
  title,
  description,
  headingLevel = 2,
  actions,
  footer,
  children,
}: CardProps) {
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';

  const classes = [
    styles.card,
    surface === 'default' ? undefined : styles[surface],
    compact ? styles.compact : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes}>
      {title || description || actions ? (
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            {title ? <Heading className={styles.title}>{title}</Heading> : null}
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
      ) : null}

      {children ? <div className={styles.body}>{children}</div> : null}
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </section>
  );
}
